import { cacheKey } from '@/constants/cache-key';
import {
	type IApiResponse,
	type IBulkRegisteredVotersResponse,
	type IElectoralDivisionMetaDataFormHook,
	type IPUinConstituencyStates,
	type IRegisteredVotersUpdate,
} from '@/helpers/types';
import { type IConstituencyLGAs } from '@/helpers/types/ILGAState';
import {
	type IConstituencyByStateIDStates,
	type IConstituencyState,
	type ILGAConstituencyStates,
	type ILGAInConstituencyStates,
	type IWardConstituencyStates,
	type IWardInConsistuency,
} from '@/interface/state/IConstituencyState';
import { getCoreQuery, type IGetQuery } from '@/store/actions/api-utils';
import {
	type ICreateUpdateConstituency,
	type IMapConstituencyLGA,
	type IMapConstituencyWard,
} from '@/store/actions/core/consistuency';

import { coreApiSlice } from '../apiSlice';

interface IUpdateWard {
	constituencyId: number;
	wardId: number;
	newConstituencyId: number;
	newWardId: number;
}

/**
 * Every read whose answer depends on which wards/LGAs a constituency holds. The mapping
 * editor invalidates this one tag after a save (`invalidateAfterMappingChange`), so the
 * detail tabs, coverage counts and the PU list refetch instead of showing the old mapping.
 */
const MAPPING_TAG = ['ConstituencyMapping' as const];

export const constituencyApiSlice = coreApiSlice.injectEndpoints({
	endpoints: (builder) => ({
		getConstituencyById: builder.query<IApiResponse<IConstituencyState>, { id: string }>({
			query: ({ id }) => `/Constituency/${id}`,
			providesTags: MAPPING_TAG,
		}),
		getConstituency: builder.query<IApiResponse<IConstituencyState[]>, IGetQuery>({
			query: ({ paged, query }) => `/ElectionCycle${getCoreQuery(paged, ...(query || []))}`,
		}),
		getConstituencyByStateId: builder.mutation<IConstituencyByStateIDStates, { stateId: number; name: string }>({
			query: (body) => ({
				method: 'POST',
				url: '/Constituency/get-constituency-by-stateid',
				body,
			}),
		}),
		deleteConstituencyById: builder.mutation<IApiResponse, { id: string }>({
			query: ({ id }) => ({
				url: `/Constituency/delete/${id}`,
				method: 'DELETE',
			}),
		}),
		updateConstituencyMetaData: builder.mutation<IApiResponse, { data: IElectoralDivisionMetaDataFormHook; id: number }>({
			query: ({ id, ...body }) => ({
				url: `/Constituency/updatemetadata/${id}`,
				method: 'PUT',
				body,
			}),
		}),
		updateConstituency: builder.mutation<IApiResponse, { data: ICreateUpdateConstituency }>({
			query: (body) => ({
				url: '/Constituency/update',
				method: 'PUT',
				body,
			}),
		}),
		createConstituency: builder.mutation<IApiResponse, { data: Omit<ICreateUpdateConstituency, 'id'> }>({
			query: (body) => ({
				url: '/Constituency',
				method: 'POST',
				body,
			}),
		}),
		getLGAForConstituency: builder.query<ILGAConstituencyStates, IGetQuery>({
			query: ({ paged, query }) => `/ConstituencyLga/get-constituencies-for-lga${getCoreQuery(paged, ...(query || []))}`,
			providesTags: MAPPING_TAG,
		}),
		getWardForConstituency: builder.query<IWardConstituencyStates, IGetQuery>({
			query: ({ paged, query }) => `/ConstituencyWard/get-constituencies-for-ward${getCoreQuery(paged, ...(query || []))}`,
			providesTags: MAPPING_TAG,
		}),
		getLGAInConstituency: builder.query<ILGAInConstituencyStates, IGetQuery>({
			query: ({ paged, query }) => `/ConstituencyLga/getLgasInConstituency${getCoreQuery(paged, ...(query || []))}`,
			providesTags: MAPPING_TAG,
		}),
		getWardsInConstituency: builder.query<IWardInConsistuency, IGetQuery>({
			query: ({ paged, query }) => `/ConstituencyWard/getWardsInConstituency${getCoreQuery(paged, ...(query || []))}`,
			providesTags: MAPPING_TAG,
		}),
		getPUsInConstituency: builder.query<IPUinConstituencyStates, IGetQuery>({
			query: ({ paged, query }) => ({
				url: `/Constituency/getPollingUnitsInConstituency${getCoreQuery(paged, ...(query || []))}`,
			}),
			providesTags: MAPPING_TAG,
		}),
		// Bulk-set registered voters per polling unit. Body is a raw array of changed rows
		// only. Super-Admin gated; all-or-nothing; errors are RFC-7807 ProblemDetails
		// (read `error.data.detail`). The coreApiSlice has no tagTypes, so refetch the PU
		// queries manually after a successful save.
		//
		// The route moved: it was `PUT /Constituency/update-polling-units` and api.core
		// renamed it to `PUT /PoolingUnit/update-registered-voters-count-by-id` (the
		// controller it always belonged on — registered voters live on `PoolingUnit`,
		// not on the constituency). Nothing here noticed, so Save on the Registered
		// voters tab had been 404ing; a 404 through RTK Query surfaces as the same
		// generic failure as a rejected batch, which is why it read as a server problem.
		// The old path now survives only in a stale doc comment on the backend DTO.
		bulkUpdateRegisteredVoters: builder.mutation<IBulkRegisteredVotersResponse, IRegisteredVotersUpdate[]>({
			query: (body) => ({
				url: '/PoolingUnit/update-registered-voters-count-by-id',
				method: 'PUT',
				body,
			}),
		}),
		mapWardInConstituency: builder.mutation<unknown, IMapConstituencyWard>({
			query: (body) => ({
				method: 'POST',
				url: '/ConstituencyWard/map-multiple-wards-to-constituency',
				body,
			}),
		}),
		mapLgaInConstituency: builder.mutation<undefined, IMapConstituencyLGA>({
			query: (body) => ({
				method: 'POST',
				url: '/ConstituencyLga/map-multiple-lgas-to-constituency',
				body,
			}),
		}),
		// Get constituency by name
		getConstituencyByName: builder.mutation<IApiResponse<IConstituencyState[]>, { name: string }>({
			query: (body) => ({
				method: 'POST',
				url: '/Constituency/name',
				body,
			}),
		}),
		// Create constituency LGA mapping
		createConstituencyLGA: builder.mutation<IApiResponse, { constituencyId: number; lgaId: number }>({
			query: (body) => ({
				method: 'POST',
				url: '/ConstituencyLga',
				body,
			}),
		}),
		// Get constituency LGAs
		getConstituencyLGA: builder.query<IConstituencyLGAs, IGetQuery>({
			query: ({ paged, query }) => `/ConstituencyLga${getCoreQuery(paged, ...(query || []))}`,
		}),
		// Create constituency ward mapping
		createConstituencyWard: builder.mutation<IApiResponse, { constituencyId: number; wardId: number }>({
			query: (body) => ({
				method: 'POST',
				url: '/ConstituencyWard',
				body,
			}),
		}),
		// Get constituency wards
		getConstituencyWard: builder.query<IApiResponse, IGetQuery>({
			query: ({ paged, query }) => `/ConstituencyWard${getCoreQuery(paged, ...(query || []))}`,
		}),
		// Update constituency ward
		updateConstituencyWard: builder.mutation<IApiResponse, IUpdateWard>({
			query: (body) => ({
				method: 'PUT',
				url: '/ConstituencyWard/update',
				body,
			}),
		}),
	}),
});

export const {
	useGetConstituencyByIdQuery,
	useLazyGetConstituencyByIdQuery,
	useGetConstituencyQuery,
	useLazyGetConstituencyQuery,
	useDeleteConstituencyByIdMutation,
	useGetConstituencyByStateIdMutation,
	useUpdateConstituencyMetaDataMutation,
	useUpdateConstituencyMutation,
	useCreateConstituencyMutation,
	useGetLGAForConstituencyQuery,
	useLazyGetLGAForConstituencyQuery,
	useGetWardForConstituencyQuery,
	useLazyGetWardForConstituencyQuery,
	useGetLGAInConstituencyQuery,
	useLazyGetLGAInConstituencyQuery,
	useGetWardsInConstituencyQuery,
	useLazyGetWardsInConstituencyQuery,
	useMapWardInConstituencyMutation,
	useMapLgaInConstituencyMutation,
	useGetConstituencyByNameMutation,
	useCreateConstituencyLGAMutation,
	useGetConstituencyLGAQuery,
	useLazyGetConstituencyLGAQuery,
	useCreateConstituencyWardMutation,
	useGetConstituencyWardQuery,
	useLazyGetConstituencyWardQuery,
	useUpdateConstituencyWardMutation,
	useGetPUsInConstituencyQuery,
	useLazyGetPUsInConstituencyQuery,
	useBulkUpdateRegisteredVotersMutation,
} = constituencyApiSlice;

export const selectGetConstituencyStateById = constituencyApiSlice.endpoints.getConstituencyByStateId.select({
	fixedCacheKey: cacheKey.GETCONSTITUENCYSTATEBYID,
	requestId: '',
});
export const selectGetConstituencyLGA = constituencyApiSlice.endpoints.getLGAInConstituency.select({});
export const selectGetWardsInConstituency = constituencyApiSlice.endpoints.getWardsInConstituency.select({});
export const selectGetConstituencyById = constituencyApiSlice.endpoints.getConstituencyById.select({
	id: '',
});
